import React, { useState, useEffect } from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import styles from "../CategoryPop/MealsPop.module.scss";
import Select from "react-select";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  useAddCategoryMutation,
  useUpdateCategoryMutation,
} from "../../../store/services/categoryService";
import { CATEGORY_TYPE } from "../../../constants/enum";

const ProductCategoryModal = ({
  title,
  modalOpen,
  setModalOpen,
  edit,
  categories,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      name: edit?.name || "",
      parentCategory: edit?.parentCategory || "",
      status: edit?.status || "",
    },
  });

  const [addCategory, { isLoading: isAdding }] = useAddCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateCategoryMutation();

  // For parent category select
  const [unit, setUnit] = useState([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);

  useEffect(() => {
    if (!categories?.data?.length) return;
    const options = categories.data.map((item) => ({
      value: item?._id,
      label: item?.name,
    }));
    setUnit(options);
    const data = edit?.parentCategory
      ? options.find((option) => edit.parentCategory === option.value)
      : null;
    setSelectedParentCategory(data);
  }, [categories?.data, edit]);

  const handleParentCategoryChange = (selectedOption) => {
    setSelectedParentCategory(selectedOption || null);
    setValue("parentCategory", selectedOption ? selectedOption.value : "");
  };

  const onSubmit = async (data) => {
    if (!edit) {
      data.type = CATEGORY_TYPE.PRODUCT;
    }
    try {
      if (edit) {
        await updateCategory({ mealId: edit._id, updatedData: data });
      } else {
        await addCategory(data).unwrap();
      }
      setModalOpen(false);
      toast.success(`Category ${edit ? "updated" : "added"} successfully`);
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const hideHandler = () => {
    setModalOpen(false);
  };

  return (
    <Modal
      show={modalOpen}
      className={`${styles.MealsPop} transparentModal`}
      onHide={hideHandler}
      backdrop={true}
      keyboard={false}
      centered
    >
      <Modal.Body className={`${styles.modalBody}`}>
        <div className="mx-auto">
          <h3 className="m-0 fw-bold themeBlue">{title}</h3>
          <Col lg="12" className="my-2">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Col lg="12" md="12" className="my-2">
                {/* Name Field */}
                <div className="py-2">
                  <label className="form-label fw-sbold text-muted ps-2 m-0">
                    Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter name"
                    className="form-control"
                    {...register("name", {
                      required: "Please enter name",
                      maxLength: {
                        value: 40,
                        message: "Name cannot exceed 40 characters",
                      },
                      validate: (value) => {
                        if (/^\s/.test(value)) {
                          return "Name cannot start with a space";
                        }
                        return true;
                      },
                    })}
                  />
                  {errors.name && (
                    <span className="text-danger">{errors.name.message}</span>
                  )}
                </div>

                {/* Parent Category (Select) - Only show when not editing */}
                {!edit && (
                  <div className="py-2">
                    <label className="form-label fw-sbold text-muted ps-2 m-0">
                      Parent Category
                    </label>
                    <Select
                      name="parentCategory"
                      options={unit}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      value={selectedParentCategory}
                      onChange={handleParentCategoryChange}
                      placeholder="Select parent category"
                    />
                  </div>
                )}

                {/* Status Field */}
                <div className="py-2">
                  <label className="form-label fw-sbold text-muted ps-2 m-0">
                    Status
                  </label>
                  <ul className="list-unstyled ps-0 mb-0 d-flex align-items-start gap-10 ps-2 flex-wrap">
                    <li className="d-flex align-items-center">
                      <input
                        type="radio"
                        value="active"
                        id="active-status"
                        {...register("status", {
                          required: "Status is required",
                        })}
                        className="form-check"
                        defaultChecked={!edit || edit.status === "active"}
                      />
                      <label
                        htmlFor="active-status"
                        className="form-label fw-sbold text-muted ps-2 m-0"
                      >
                        Active
                      </label>
                    </li>
                    <li className="d-flex align-items-center">
                      <input
                        type="radio"
                        value="inactive"
                        id="inactive-status"
                        {...register("status", {
                          required: "Status is required",
                        })}
                        className="form-check"
                        defaultChecked={edit && edit.status === "inactive"}
                      />
                      <label
                        htmlFor="inactive-status"
                        className="form-label fw-sbold text-muted ps-2 m-0"
                      >
                        Inactive
                      </label>
                    </li>
                  </ul>
                  {errors.status && (
                    <span className="text-danger">{errors.status.message}</span>
                  )}
                </div>
              </Col>

              {/* Submit & Cancel Buttons */}
              <Col lg="12" className="my-2">
                <div className="d-flex align-items-center justify-content-center gap-10">
                  <Button
                    className="d-flex align-items-center justify-content-center commonBtn"
                    type="submit"
                    disabled={isAdding || isUpdating}
                  >
                    Submit
                  </Button>
                  <Button
                    className="d-flex align-items-center justify-content-center commonBtn GreyBtn"
                    type="button"
                    onClick={hideHandler}
                  >
                    Cancel
                  </Button>
                </div>
              </Col>
            </Form>
          </Col>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProductCategoryModal;
